import { hashValue } from './hashing';
import {
  BalanceSchema,
  CAMPS,
  MapFileSchema,
  ScenarioSchema,
  type Balance,
  type Camp,
  type MapFile,
  type Scenario,
} from './schemas';

export type CellKind = 'EMPTY' | 'BLOCK' | 'BASE';

/** Zero-based cell coordinate. Cell (col,row) spans x in [col, col+1], y in [row, row+1]. */
export interface Cell {
  readonly col: number;
  readonly row: number;
}

export interface Point {
  readonly x: number;
  readonly y: number;
}

/**
 * Validated, parsed arena. All coordinates are zero-based and continuous:
 * the arena spans x in [0, columns] and y in [0, rows], ORANGE at the top (small y).
 */
export interface ArenaMap {
  readonly id: string;
  readonly version: number;
  readonly name: string;
  readonly columns: number;
  readonly rows: number;
  /** Row-major cell kinds, index = row * columns + col. */
  readonly cells: readonly CellKind[];
  /** Base owner per cell index, or null. */
  readonly baseOwner: readonly (Camp | null)[];
  readonly blocks: readonly Cell[];
  readonly bases: Readonly<Record<Camp, readonly Cell[]>>;
  readonly blockCollisionSize: number;
  readonly playerSpawns: Readonly<Record<Camp, Point>>;
  readonly goldenSpawns: readonly Cell[];
  readonly forbiddenSpawnRows: readonly number[];
  readonly hash: string;
}

export class ContentError extends Error {
  readonly problems: readonly string[];
  constructor(what: string, problems: readonly string[]) {
    super(`${what} is invalid:\n- ${problems.join('\n- ')}`);
    this.name = 'ContentError';
    this.problems = problems;
  }
}

export function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const key of Object.keys(value)) {
      deepFreeze((value as Record<string, unknown>)[key]);
    }
  }
  return value;
}

function zodProblems(error: { issues: readonly { path: PropertyKey[]; message: string }[] }) {
  return error.issues.map((i) => `${i.path.map(String).join('.') || '(root)'}: ${i.message}`);
}

export function parseBalance(raw: unknown): Balance {
  const result = BalanceSchema.safeParse(raw);
  if (!result.success) throw new ContentError('balance', zodProblems(result.error));
  const balance = result.data;
  const problems: string[] = [];
  if (balance.player.hardSpeedCap < balance.player.maxInputSpeed) {
    problems.push('player.hardSpeedCap must be >= player.maxInputSpeed');
  }
  if (balance.golden.maxSpeed < balance.golden.cruiseSpeed) {
    problems.push('golden.maxSpeed must be >= golden.cruiseSpeed');
  }
  if (balance.fuse.chainDelaySeconds >= balance.fuse.fuseSeconds) {
    problems.push('fuse.chainDelaySeconds must be shorter than the fuse');
  }
  if (balance.explosion.visualSeconds < balance.explosion.lethalSeconds) {
    problems.push('explosion.visualSeconds must be >= explosion.lethalSeconds');
  }
  if (problems.length > 0) throw new ContentError('balance', problems);
  return deepFreeze(balance);
}

const TOKENS: Record<string, { kind: CellKind; owner: Camp | null }> = {
  '.': { kind: 'EMPTY', owner: null },
  '#': { kind: 'BLOCK', owner: null },
  O: { kind: 'BASE', owner: 'ORANGE' },
  T: { kind: 'BASE', owner: 'TURQUOISE' },
};

export function parseMap(raw: unknown): ArenaMap {
  const result = MapFileSchema.safeParse(raw);
  if (!result.success) throw new ContentError('map', zodProblems(result.error));
  const file: MapFile = result.data;
  const { columns, rows } = file.grid;
  const problems: string[] = [];

  const cells: CellKind[] = [];
  const baseOwner: (Camp | null)[] = [];
  if (file.layout.length !== rows) {
    problems.push(`layout has ${file.layout.length} rows, expected ${rows}`);
  }
  file.layout.forEach((line, row) => {
    const tokens = line.trim().split(/\s+/);
    if (tokens.length !== columns) {
      problems.push(`layout row ${row + 1} has ${tokens.length} cells, expected ${columns}`);
    }
    tokens.forEach((token) => {
      const parsed = TOKENS[token];
      if (!parsed) problems.push(`layout row ${row + 1}: unknown token "${token}"`);
      cells.push(parsed?.kind ?? 'EMPTY');
      baseOwner.push(parsed?.owner ?? null);
    });
  });
  if (problems.length > 0) throw new ContentError(`map "${file.id}"`, problems);

  const kindAt = (col: number, row: number) => cells[row * columns + col];
  const blocks: Cell[] = [];
  const bases: Record<Camp, Cell[]> = { ORANGE: [], TURQUOISE: [] };
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < columns; col++) {
      const index = row * columns + col;
      if (cells[index] === 'BLOCK') blocks.push({ col, row });
      const owner = baseOwner[index];
      if (owner) bases[owner].push({ col, row });
    }
  }

  for (const camp of CAMPS) {
    const cellsOfBase = bases[camp];
    if (cellsOfBase.length !== 2) {
      problems.push(`${camp} base must occupy exactly 2 cells (found ${cellsOfBase.length})`);
    } else {
      const [a, b] = cellsOfBase as [Cell, Cell];
      if (a.row !== b.row || Math.abs(a.col - b.col) !== 1) {
        problems.push(`${camp} base cells must be horizontally adjacent`);
      }
      const expectedRow = camp === 'ORANGE' ? 0 : rows - 1;
      if (a.row !== expectedRow) problems.push(`${camp} base must be on row ${expectedRow + 1}`);
    }
  }

  // Fairness: the map must be point-symmetric (180 degree rotation swaps the camps).
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < columns; col++) {
      const k = kindAt(col, row);
      const mirror = kindAt(columns - 1 - col, rows - 1 - row);
      const owner = baseOwner[row * columns + col];
      const mirrorOwner = baseOwner[(rows - 1 - row) * columns + (columns - 1 - col)];
      const ownerOk =
        owner === null ? mirrorOwner === null : mirrorOwner !== null && mirrorOwner !== owner;
      if (k !== mirror || !ownerOk) {
        problems.push(`layout is not point-symmetric at cell [${col + 1},${row + 1}]`);
      }
    }
  }

  const o = file.players.ORANGE;
  const t = file.players.TURQUOISE;
  if (Math.abs(o.x - (columns - t.x)) > 1e-9 || Math.abs(o.y - (rows - t.y)) > 1e-9) {
    problems.push('player spawns must be point-symmetric');
  }
  for (const camp of CAMPS) {
    const p = file.players[camp];
    if (p.x <= 0 || p.x >= columns || p.y <= 0 || p.y >= rows) {
      problems.push(`${camp} player spawn is outside the arena`);
    } else if (kindAt(Math.floor(p.x), Math.floor(p.y)) !== 'EMPTY') {
      problems.push(`${camp} player spawn is inside a solid cell`);
    }
  }

  const goldenSpawns: Cell[] = [];
  const seen = new Set<string>();
  for (const [c, r] of file.goldenSpawns) {
    const col = c - 1;
    const row = r - 1;
    if (col < 0 || col >= columns || row < 0 || row >= rows) {
      problems.push(`golden spawn [${c},${r}] is outside the grid`);
      continue;
    }
    if (kindAt(col, row) !== 'EMPTY') problems.push(`golden spawn [${c},${r}] is not a free cell`);
    if (seen.has(`${col},${row}`)) problems.push(`golden spawn [${c},${r}] is duplicated`);
    seen.add(`${col},${row}`);
    goldenSpawns.push({ col, row });
  }
  for (const cell of goldenSpawns) {
    if (!seen.has(`${columns - 1 - cell.col},${rows - 1 - cell.row}`)) {
      problems.push(`golden spawn [${cell.col + 1},${cell.row + 1}] has no point-symmetric twin`);
    }
  }

  const forbiddenSpawnRows = file.forbiddenSpawnRows.map((r) => r - 1);
  for (const r of forbiddenSpawnRows) {
    if (r < 0 || r >= rows) problems.push(`forbidden spawn row ${r + 1} is outside the grid`);
  }

  // Every free cell must be reachable from every other free cell (4-connectivity).
  const free = cells.map((k) => k === 'EMPTY');
  const start = free.indexOf(true);
  if (start >= 0) {
    const visited = new Array<boolean>(cells.length).fill(false);
    const queue = [start];
    visited[start] = true;
    while (queue.length > 0) {
      const index = queue.shift() as number;
      const col = index % columns;
      const row = Math.floor(index / columns);
      const neighbours: [number, number][] = [
        [col - 1, row],
        [col + 1, row],
        [col, row - 1],
        [col, row + 1],
      ];
      for (const [nc, nr] of neighbours) {
        if (nc < 0 || nc >= columns || nr < 0 || nr >= rows) continue;
        const ni = nr * columns + nc;
        if (free[ni] && !visited[ni]) {
          visited[ni] = true;
          queue.push(ni);
        }
      }
    }
    const unreachable = free.filter((f, i) => f && !visited[i]).length;
    if (unreachable > 0) problems.push(`${unreachable} free cells are unreachable`);
  }

  if (problems.length > 0) throw new ContentError(`map "${file.id}"`, problems);

  return deepFreeze({
    id: file.id,
    version: file.version,
    name: file.name,
    columns,
    rows,
    cells,
    baseOwner,
    blocks,
    bases,
    blockCollisionSize: file.blockCollisionSize,
    playerSpawns: { ORANGE: { ...o }, TURQUOISE: { ...t } },
    goldenSpawns,
    forbiddenSpawnRows,
    hash: hashValue(file),
  });
}

/** Cross-checks that need both the map and the balance. */
export function validateContent(map: ArenaMap, balance: Balance): void {
  const problems: string[] = [];
  if (map.goldenSpawns.length < balance.golden.count) {
    problems.push(
      `map "${map.id}" has ${map.goldenSpawns.length} golden spawns, balance needs ${balance.golden.count}`,
    );
  }
  for (const camp of CAMPS) {
    const p = map.playerSpawns[camp];
    const r = balance.player.radius;
    if (p.x - r < 0 || p.x + r > map.columns || p.y - r < 0 || p.y + r > map.rows) {
      problems.push(`${camp} player spawn overlaps a wall`);
    }
    const inset = (1 - map.blockCollisionSize) / 2;
    for (let row = 0; row < map.rows; row++) {
      for (let col = 0; col < map.columns; col++) {
        const kind = map.cells[row * map.columns + col];
        if (kind === 'EMPTY') continue;
        const pad = kind === 'BLOCK' ? inset : 0;
        const cx = Math.max(col + pad, Math.min(p.x, col + 1 - pad));
        const cy = Math.max(row + pad, Math.min(p.y, row + 1 - pad));
        if ((p.x - cx) ** 2 + (p.y - cy) ** 2 < r * r) {
          problems.push(`${camp} player spawn overlaps solid cell [${col + 1},${row + 1}]`);
        }
      }
    }
  }
  if (problems.length > 0) throw new ContentError('content', problems);
}

export function parseScenario(raw: unknown): Scenario {
  const result = ScenarioSchema.safeParse(raw);
  if (!result.success) throw new ContentError('scenario', zodProblems(result.error));
  return deepFreeze(result.data);
}
